/**
 * Server certificates for recording a phone's traffic (docs/design/mobile-recorder.md "Server certificates"): a
 * performance project can hold the TLS certificate and private key of its own API servers (test environments). While a
 * phone records, the Device Agent's recording proxy opens those hosts with the real certificate, so the phone needs no
 * 1QA certificate and the app no special build (release builds and certificate pinning work too). Other hosts still
 * use the agent's own 1QA Recorder CA.
 */
import { z } from 'zod';

/** Certificates per performance project. */
export const SERVER_CERTS_MAX = 20;

const PEM_BLOCK = /-----BEGIN ([A-Z0-9 ]+)-----[\s\S]+?-----END \1-----/g;

/** PEM blocks of a text by type: certificates (in order: the server's first, then intermediates) and private keys. */
export function pemBlocks(text: string): { certs: string[]; keys: string[] } {
  const certs: string[] = [];
  const keys: string[] = [];
  for (const m of text.matchAll(PEM_BLOCK)) {
    const block = `${m[0].trim()}\n`;
    if (m[1] === 'CERTIFICATE') certs.push(block);
    else if (/PRIVATE KEY$/.test(m[1]!)) keys.push(block);
  }
  return { certs, keys };
}

/**
 * POST /projects/:id/perf/server-certs. `certificate`: the server's certificate followed by its intermediates (PEM);
 * `privateKey`: its key (PEM, unencrypted). Both may also be pasted together into `certificate`.
 */
export const serverCertInputSchema = z.object({
  name: z.string().trim().max(100).optional(),
  certificate: z.string().min(1).max(64_000),
  privateKey: z.string().max(16_000).default(''),
});
export type ServerCertInput = z.input<typeof serverCertInputSchema>;

export interface ServerCertView {
  id: string;
  name: string;
  /** Host names it is valid for (wildcards as "*.example.com") and IP addresses. */
  hosts: string[];
  issuer: string;
  notAfter: string;
  expired: boolean;
  /** SHA-256 fingerprint of the server's certificate. */
  sha256: string;
  /** It covers the project's app address. */
  coversAppHost: boolean;
  createdBy: { id: string; name: string };
  createdAt: string;
}

/** A certificate as the Device Agent gets it in a `record-start` job (kept in memory there, never written). */
export interface ServerCertForAgent {
  hosts: string[];
  /** The server's certificate and its intermediates. */
  certPem: string;
  keyPem: string;
}

/**
 * Whether a certificate name covers a host, as TLS clients check it: exact (case-insensitive), or a wildcard for
 * exactly one label ("*.example.com" covers "api.example.com", not "example.com" or "a.b.example.com").
 */
export function certHostMatches(pattern: string, host: string): boolean {
  const p = pattern.toLowerCase().replace(/\.$/, '');
  const h = host.toLowerCase().replace(/\.$/, '');
  if (p === h) return true;
  if (!p.startsWith('*.')) return false;
  const rest = p.slice(2);
  const dot = h.indexOf('.');
  return dot > 0 && h.slice(dot + 1) === rest;
}

/** The certificate of `certs` that covers `host`, if any. */
export function serverCertFor<C extends { hosts: string[] }>(certs: C[], host: string): C | undefined {
  return certs.find((c) => c.hosts.some((p) => certHostMatches(p, host)));
}
